// iter687 — First-Run Country Setup Wizard.
//
// Shown on the dashboard when `branding.setup_complete !== true`.
// Three steps, ~30 seconds end-to-end:
//
//   1. Pick country (defaults are inferred from current settings if
//      we can detect them: e.g. VAT 15% + SAR currency → KSA).
//   2. Confirm company info (Name, VAT, CR, Address, Phone, Email)
//      — prefilled with whatever the operator already typed into
//      Branding Settings. Single Source of Truth.
//   3. Confirm receipt header and review the applied defaults
//      (VAT %, ZATCA on/off, currency, language). One click "Apply".
//
// Crucially: nothing the operator already entered gets overwritten
// unless they confirm. Existing tenants see the wizard ONCE on
// next login and can dismiss it ("Set up later") — the system
// keeps nudging them gently from the dashboard banner until done.

import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { COUNTRIES, getCountry } from '@/lib/countries';
import api from '@/lib/api';
import { toast } from 'sonner';

const STEPS = ['country', 'company', 'review'];

/**
 * Heuristic: if the tenant already has SAR + VAT 15%, they're almost
 * certainly KSA. Try to pre-select to avoid extra clicks. Returns ''
 * when no strong signal — operator picks manually.
 */
function inferCountry(branding) {
  const vat = String(branding?.company_vat || '');
  // KSA VAT registration numbers are 15 digits starting with 3
  if (/^3\d{14}$/.test(vat)) return 'SA';
  const sym = (branding?.currency_symbol || '').toLowerCase();
  if (sym.includes('﷼') || sym === 'sar') return 'SA';
  if (sym === 'aed') return 'AE';
  if (sym === 'kd') return 'KW';
  if (sym === '$') return 'US';
  if (sym === '£') return 'GB';
  if (sym === '₹') return 'IN';
  return '';
}

export function CountrySetupWizard({ open, onOpenChange, onDone, initialBranding }) {
  const [step, setStep] = useState(0);
  const [code, setCode] = useState('');
  const [form, setForm] = useState({
    company_name: '', company_vat: '', cr_number: '',
    company_address: '', company_phone: '', company_email: '',
    receipt_header_text: '',
  });
  const [saving, setSaving] = useState(false);

  // Reset when dialog opens — pulls fresh values from current branding.
  useEffect(() => {
    if (!open) return;
    const b = initialBranding || {};
    setStep(0);
    setCode(b.country_code || inferCountry(b));
    setForm({
      company_name: b.company_name || '',
      company_vat: b.company_vat || '',
      cr_number: b.cr_number || '',
      company_address: b.company_address || '',
      company_phone: b.company_phone || '',
      company_email: b.company_email || '',
      receipt_header_text: b.receipt_header_text || '',
    });
  }, [open, initialBranding]);

  const country = getCountry(code);

  // When operator picks a new country and they haven't already set a
  // receipt header, prefill the country's default. Don't clobber a
  // header they explicitly typed in.
  useEffect(() => {
    if (!country) return;
    setForm(f => f.receipt_header_text
      ? f
      : { ...f, receipt_header_text: country.receipt_header_default || '' });
  }, [code, country]);

  const canNext = useMemo(() => {
    if (step === 0) return !!code;
    if (step === 1) return !!form.company_name.trim();
    return true;
  }, [step, code, form.company_name]);

  const save = async () => {
    if (!country) { toast.error('Please pick a country'); return; }
    setSaving(true);
    try {
      // Merge into existing branding — do NOT overwrite fields we
      // never asked about in the wizard.
      const payload = {
        ...(initialBranding || {}),
        ...form,
        country_code: country.code,
        setup_complete: true,
      };
      await api.post('/pdf-exports/branding', payload);
      toast.success(`${country.flag} Setup complete — ${country.name} defaults applied.`);
      onDone?.(payload);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save setup');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="country-setup-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe size={18} className="text-emerald-600" />
            Welcome — let&apos;s set up your country in 30 seconds
          </DialogTitle>
          <div className="flex items-center gap-1 pt-2">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? 'w-12 bg-emerald-500' : i < step ? 'w-6 bg-emerald-300' : 'w-6 bg-zinc-200'}`}
                data-testid={`wizard-step-dot-${i}`}
              />
            ))}
            <span className="text-[10px] text-muted-foreground ml-2">Step {step + 1} of {STEPS.length}</span>
          </div>
        </DialogHeader>

        <div className="pt-2 min-h-[320px]">
          {step === 0 && (
            <div className="space-y-3" data-testid="wizard-step-country">
              <p className="text-sm text-stone-700">
                Pick the country where you mainly operate. We&apos;ll auto-enable the
                right tax rules, currency, and compliance options — and hide what
                doesn&apos;t apply.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                {COUNTRIES.map(c => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setCode(c.code)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-left transition ${
                      code === c.code
                        ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                        : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                    }`}
                    data-testid={`wizard-country-${c.code}`}
                  >
                    <span className="text-2xl">{c.flag}</span>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-stone-800 truncate">{c.name}</div>
                      <div className="text-[10px] text-stone-500">{c.currency_code} · {c.vat_label} {c.default_vat_rate}%</div>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">
                Tip: each branch can pick its own country later — useful for multi-country chains.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-company">
              <p className="text-sm text-stone-700">
                Confirm your business identity. We&apos;ll reuse this everywhere — invoices, receipts,
                exports — so you never have to retype it.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Company name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder={country ? `e.g. ${country.name === 'Saudi Arabia' ? 'Al Salam Trading Co.' : 'My Company LLC'}` : 'My Company'}
                    data-testid="wizard-company-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">{country?.vat_label || 'VAT'} number</Label>
                  <Input
                    value={form.company_vat}
                    onChange={(e) => setForm({ ...form, company_vat: e.target.value })}
                    placeholder={country?.code === 'SA' ? '300000000003' : 'e.g. 12-3456789'}
                    data-testid="wizard-company-vat"
                  />
                </div>
                <div>
                  <Label className="text-xs">CR number</Label>
                  <Input
                    value={form.cr_number}
                    onChange={(e) => setForm({ ...form, cr_number: e.target.value })}
                    placeholder="Commercial Registration"
                    data-testid="wizard-company-cr"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Address</Label>
                  <Textarea
                    rows={2}
                    value={form.company_address}
                    onChange={(e) => setForm({ ...form, company_address: e.target.value })}
                    placeholder="Street, City, Postal Code"
                    data-testid="wizard-company-address"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={form.company_phone}
                    onChange={(e) => setForm({ ...form, company_phone: e.target.value })}
                    placeholder="+966 5x xxx xxxx"
                    data-testid="wizard-company-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={form.company_email}
                    onChange={(e) => setForm({ ...form, company_email: e.target.value })}
                    placeholder="contact@yourcompany.com"
                    data-testid="wizard-company-email"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && country && (
            <div className="space-y-3" data-testid="wizard-step-review">
              <p className="text-sm text-stone-700">
                Review what we&apos;ll apply. You can change any of this later in Settings.
              </p>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{country.flag}</span>
                  <div>
                    <div className="font-semibold text-stone-800">{country.name}</div>
                    <div className="text-[11px] text-stone-600">{country.region}</div>
                  </div>
                </div>
                <ul className="text-[12px] text-stone-700 space-y-1 pt-2">
                  <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Currency: <strong>{country.currency_code}</strong> ({country.currency_symbol})</li>
                  <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Default {country.vat_label} rate: <strong>{country.default_vat_rate}%</strong></li>
                  {country.has_zatca_phase1 && (
                    <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> ZATCA Phase 1 QR available (opt-in via Settings → Receipt)</li>
                  )}
                  {country.has_zakat && (
                    <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Zakat module enabled</li>
                  )}
                  {country.has_fta_uae && (
                    <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> UAE FTA tax invoice format</li>
                  )}
                  <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-600" /> Date format: <strong>{country.date_format}</strong></li>
                </ul>
              </div>
              <div>
                <Label className="text-xs">Receipt header (printed at top of every slip)</Label>
                <Textarea
                  rows={2}
                  value={form.receipt_header_text}
                  onChange={(e) => setForm({ ...form, receipt_header_text: e.target.value })}
                  placeholder={country.receipt_header_default}
                  data-testid="wizard-receipt-header"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Pre-filled with the recommended format for {country.name}. Edit freely.
                </p>
              </div>
              <div className="text-[10px] text-stone-500 leading-snug pt-1 border-t border-stone-200">
                ✓ This will <strong>not</strong> change your existing branches, transactions or reports. It only sets defaults for future settings.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 pt-3 border-t border-zinc-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="wizard-skip-btn"
          >
            Set up later
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(step - 1)}
                data-testid="wizard-back-btn"
              >
                <ChevronLeft size={14} className="mr-1" /> Back
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button
                size="sm"
                disabled={!canNext}
                onClick={() => setStep(step + 1)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-next-btn"
              >
                Next <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button
                size="sm"
                disabled={saving}
                onClick={save}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="wizard-finish-btn"
              >
                {saving ? 'Saving…' : 'Apply & finish'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
